import { EditorData, UndoableEditorData } from './Proto/dcei_editor_data_proto';

/**
 * @typedef {Object} StoreDataAction
 * @property {string} type
 * @property {Object} action
 * @property {Object} undoable
 * @property {Object} redo
 * @property {Object} undo
 * @property {Object} reset
 */

export class StoreData {
    constructor(other) {
        if (other != null) {
            Object.assign(this, other);
            return;
        }
        this.data = new EditorData();
        this.undoable = {
            current: new UndoableEditorData(),
            past: [],
            future: [],
        };
    }

    /**
     * @param {EditorData} data
     * @param {UndoableEditorData} undoable
     * @returns {StoreData}
     */
    static create(data, undoable) {
        const result = new StoreData();
        result.data = data;
        result.undoable.current = undoable;
        return result;
    }

    static fromAny(value) {
        if (value instanceof StoreData) {
            return value;
        }
        return StoreData.fromJson(value);
    }

    static fromJson(json) {
        const result = new StoreData();
        result.data = EditorData.fromJson(json.data);
        result.undoable = {
            current: UndoableEditorData.fromJson(json.undoable.current),
            past: json.undoable.past.map(i => UndoableEditorData.fromJson(i)),
            future: json.undoable.future.map(i => UndoableEditorData.fromJson(i)),
        };
        return result;
    }

    toJsonOnlyCurrent() {
        return {
            data: this.data.toJson(),
            undoable: {
                current: this.undoable.current.toJson(),
                past: [],
                future: [],
            }
        };
    }

    /**
     * @param {StoreDataAction} action 
     */
    apply(action) {
        // Apply an action recursively (i.e., a batch action can contain other batch actions).
        function applyActionRecursively(data, action) {
            if (action.type === 'BATCH') {
                for (let item of action.actions) {
                    data = applyActionRecursively(data, item);
                }
            } else {
                data = data.apply(action.type, action.path, action.value);
            }
            return data;
        }
        if (action.action) {
            const newData = applyActionRecursively(this.data, action.action);
            if (newData != this.data) {
                const copy = new StoreData(this);
                copy.data = newData;
                return copy;
            }
            return this;
        }
        if (action.undoable) {
            let newCurrent = applyActionRecursively(this.undoable.current, action.undoable);
            if (newCurrent != this.undoable.current) {
                const copy = new StoreData(this);
                copy.undoable = {
                    current: newCurrent,
                    past: this.undoable.past.concat(this.undoable.current),
                    future: []
                };
                return copy;
            }
            return this;
        }
        if (action.undo) {
            if (this.undoable.past.length > 0) {
                const copy = new StoreData(this);
                copy.undoable = {
                    current: this.undoable.past[this.undoable.past.length - 1],
                    past: this.undoable.past.slice(0, this.undoable.past.length - 1),
                    future: this.undoable.future.concat(this.undoable.current),
                };
                return copy;
            }
            return this;
        }
        if (action.redo) {
            if (this.undoable.future.length > 0) {
                const copy = new StoreData(this);
                copy.undoable = {
                    current: this.undoable.future[this.undoable.future.length - 1],
                    past: this.undoable.past.concat(this.undoable.current),
                    future: this.undoable.future.slice(0, this.undoable.future.length - 1),
                };
                return copy;
            }
            return this;
        }
        if (action.reset) {
            return StoreData.fromAny(action.reset);
        }
        if (action.type.startsWith("@@redux")) {
            return this;
        }
        console.log('Unknown action: ', action);
        return this;
    }
}
